import { useState, useCallback } from 'react'

/**
 * Custom hook for managing dialog state
 * Reduces boilerplate dialog open/close logic
 */
export const useDialog = (initialOpen = false) => {
  const [open, setOpen] = useState(initialOpen)
  const [data, setData] = useState(null)

  const openDialog = useCallback((dialogData = null) => {
    setData(dialogData)
    setOpen(true)
  }, [])

  const closeDialog = useCallback(() => {
    setOpen(false)
    setData(null)
  }, [])

  const toggleDialog = useCallback((shouldOpen) => {
    if (typeof shouldOpen === 'boolean') {
      setOpen(shouldOpen)
    } else {
      setOpen((prev) => !prev)
    }
  }, [])

  return {
    open,
    data,
    openDialog,
    closeDialog,
    toggleDialog,
    setData,
  }
}

/**
 * Custom hook for managing multiple dialogs
 * Useful when component has multiple dialogs
 */
export const useDialogs = (initialDialogs = {}) => {
  const [dialogs, setDialogs] = useState({
    edit: false,
    delete: false,
    help: false,
    draft: false,
    ...initialDialogs,
  })

  const [dialogData, setDialogData] = useState({})

  const openDialog = useCallback((dialogName, data = null) => {
    setDialogs((prev) => ({ ...prev, [dialogName]: true }))
    if (data) {
      setDialogData((prev) => ({ ...prev, [dialogName]: data }))
    }
  }, [])

  const closeDialog = useCallback((dialogName) => {
    setDialogs((prev) => ({ ...prev, [dialogName]: false }))
    setDialogData((prev) => {
      const newData = { ...prev }
      delete newData[dialogName]
      return newData
    })
  }, [])

  const toggleDialog = useCallback((dialogName) => {
    setDialogs((prev) => ({ ...prev, [dialogName]: !prev[dialogName] }))
  }, [])

  const isDialogOpen = useCallback((dialogName) => dialogs[dialogName] || false, [dialogs])

  const getDialogData = useCallback((dialogName) => dialogData[dialogName] || null, [dialogData])

  return {
    dialogs,
    dialogData,
    openDialog,
    closeDialog,
    toggleDialog,
    isDialogOpen,
    getDialogData,
  }
}
